import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePagination, useUpdateQueryParams } from 'vopay';
import useFetchPartnerAccounts, {
  toPartnerAccountStatusFilter,
  type FetchPartnerAccountsResponse,
  type PartnerAccountsRequest,
} from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';

/** Rows per page, sent with the request and used to size the paginator. */
const PAGE_SIZE = 20;

type QueryParams = {
  country: string;
  status: string;
  searchText: string;
  currency: string;
};

// "All" is a selection, not a filter, and the query string keeps it as one — so the flags below
// have to discount it or the UI reports filters nobody applied.
const isFiltered = (value: string) => value !== '' && value.toLocaleLowerCase() !== 'all';

type UseAccountsListingProps = {
  /** Another partner account's token: lists that partner account's book instead of the session's. */
  bearer?: string;
  /** Says what failed in the host's own words; the rows are already cleared when it runs. */
  onLoadError: (error: unknown) => void;
};

export type AccountsListing = ReturnType<typeof useAccountsListing>;

/**
 * One page of a partner's book of accounts, filtered by the query string — the state behind
 * `AccountFilters` and `AccountsTable`, which the Accounts page and the partner page's Accounts tab
 * both compose. The filters live in the URL so a reload or a shared link keeps them.
 */
export default function useAccountsListing({ bearer, onLoadError }: UseAccountsListingProps) {
  const location = useLocation();
  const updateQueryParams = useUpdateQueryParams<QueryParams>();
  const { response, fetching: fetchingAccounts, fetchPartnerAccounts, reset } = useFetchPartnerAccounts({ bearer });

  // The hook's `fetching` is false until the effect below runs; without this the table would
  // render its resolved empty state for one frame first.
  const [hasLoaded, setHasLoaded] = useState(false);

  // The partner's program terms ride along on every page of the listing, but they are partner-level and
  // do not change with the filters — so they are kept from the first answer, and the card is not put
  // back into its loading state on every filter, page or tile click.
  const [programTerms, setProgramTerms] = useState<Pick<
    FetchPartnerAccountsResponse,
    'RevShare' | 'MinimumProgramFee'
  > | null>(null);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const country = queryParams.get('country') || '';
  const status = toPartnerAccountStatusFilter(queryParams.get('status'));
  const currency = queryParams.get('currency') || '';
  const searchText = queryParams.get('searchText') || '';

  // Active alone keeps the nested tree: it is the list's resting state, not a search.
  const hasSearch =
    (isFiltered(status) && status !== 'Active') || isFiltered(country) || isFiltered(currency) || searchText !== '';

  const filtersApplied = isFiltered(status) || isFiltered(country) || isFiltered(currency) || searchText !== '';

  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(
    response?.PaginationMetaData?.TotalRecords || 0,
    {
      pageSize: PAGE_SIZE,
    },
  );

  // The listing's own query; also what a created account or a completed transfer re-runs so the rows
  // and balances on screen are current.
  const loadAccounts = () =>
    fetchPartnerAccounts({
      Country: country,
      VopayAccountStatus: status,
      SearchText: searchText,
      Currency: currency,
      Limit: PAGE_SIZE,
      Offset: offset,
    })
      .then(({ RevShare, MinimumProgramFee }) => {
        setProgramTerms((current) => current ?? { RevShare, MinimumProgramFee });
      })
      .catch((error) => {
        // Clearing the rows alone would leave the previous response feeding the paginator and the
        // program-terms figures, so an empty table would sit beside a page count from the last query.
        reset();
        onLoadError(error);
      })
      .finally(() => setHasLoaded(true));

  useEffect(() => {
    loadAccounts();
  }, [bearer, country, status, searchText, currency, offset]);

  function applyFilters(values: PartnerAccountsRequest) {
    setPageIndex(0);

    updateQueryParams({
      country: values.Country || '',
      status: values.VopayAccountStatus || '',
      searchText: values.SearchText || '',
      currency: values.Currency || '',
    });
  }

  // A breadcrumb in the flat (search) table narrows the list to that one account.
  function searchForAccount(accountName: string) {
    setPageIndex(0);

    updateQueryParams({
      searchText: accountName,
      country: '',
      currency: '',
      status: '',
    });
  }

  return {
    response,
    programTerms,
    balances: response?.balances ?? [],
    fetchingAccounts,
    hasLoaded,
    filters: { country, status, currency, searchText },
    filtersApplied,
    hasSearch,
    pageCount,
    pageIndex,
    setPageIndex,
    applyFilters,
    searchForAccount,
    loadAccounts,
  };
}
